import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { userTemplatesAPI, enumsAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Save,
  ClipboardList,
  Gauge,
  Calendar,
  ChevronRight,
  Edit,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function CustomTemplates() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  const [form, setForm] = useState({
    name: "",
    description: "",
    services: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        userTemplatesAPI.list(),
        enumsAPI.serviceCategories(),
      ]);
      setTemplates(templatesRes.data.templates);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setForm({
        name: template.name,
        description: template.description || "",
        services: template.services || [],
      });
    } else {
      setEditingTemplate(null);
      setForm({
        name: "",
        description: "",
        services: [],
      });
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingTemplate(null);
    setForm({ name: "", description: "", services: [] });
  };

  const addService = () => {
    setForm((prev) => ({
      ...prev,
      services: [
        ...prev.services,
        { category: "", title: "", interval_miles: "", interval_months: "" },
      ],
    }));
  };

  const updateService = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      ),
    }));
  };

  const removeService = (index) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (form.services.length === 0) {
      toast.error("Add at least one service");
      return;
    }
    
    // Validate services
    for (const service of form.services) {
      if (!service.category || !service.title) {
        toast.error("All services must have category and title");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        services: form.services.map((s) => ({
          category: s.category,
          title: s.title,
          interval_miles: s.interval_miles ? parseInt(s.interval_miles) : null,
          interval_months: s.interval_months ? parseInt(s.interval_months) : null,
        })),
      };

      if (editingTemplate) {
        await userTemplatesAPI.update(editingTemplate.id, payload);
        toast.success("Template updated");
      } else {
        await userTemplatesAPI.create(payload);
        toast.success("Template created");
      }
      
      closeEditor();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    try {
      await userTemplatesAPI.delete(templateId);
      toast.success("Template deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="back-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />

                <h1 className="text-lg font-bold text-foreground">
                  My Templates
                </h1>
              </div>
            </div>
            <Button
              onClick={() => openEditor()}
              data-testid="create-template-btn"
              className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {templates.length === 0 ? (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-zinc-900 rounded-sm flex items-center justify-center mb-4">
                <ClipboardList className="w-8 h-8 text-zinc-600" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                No Custom Templates
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm">
                Create your own maintenance schedule templates with custom intervals
              </p>
              <Button
                onClick={() => openEditor()}
                data-testid="create-first-template-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {templates.map((template) => (
              <Card
                key={template.id}
                className="card-tactical bg-zinc-950/50 border-zinc-800"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-bold text-foreground">{template.name}</h3>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {template.description}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        {template.service_count || template.services?.length || 0} service items
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditor(template)}
                        data-testid={`edit-template-${template.id}`}
                        className="border-zinc-700 hover:bg-zinc-800"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`delete-template-${template.id}`}
                            className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-zinc-950 border-zinc-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete "{template.name}". This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-zinc-900 border-zinc-800">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(template.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Template Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
            <DialogDescription>
              Define a maintenance schedule with service items and intervals
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Template Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  data-testid="template-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., My Heavy Duty Schedule"
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="template-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe when to use this template..."
                  className="bg-zinc-900 border-zinc-800 min-h-[60px]"
                />
              </div>
            </div>

            {/* Services List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Services ({form.services.length})</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addService}
                  data-testid="add-service-btn"
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Service
                </Button>
              </div>

              {form.services.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-zinc-800 rounded-sm">
                  No services added yet. Click "Add Service" to start.
                </div>
              ) : (
                <div className="space-y-3">
                  {form.services.map((service, index) => (
                    <div
                      key={index}
                      className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 space-y-3"
                    >
                      <div className="flex items-start justify-between">
                        <span className="text-xs text-muted-foreground">
                          Service #{index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeService(index)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Category *</Label>
                          <Select
                            value={service.category}
                            onValueChange={(v) => updateService(index, "category", v)}
                          >
                            <SelectTrigger className="bg-zinc-950 border-zinc-800 h-9">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-zinc-800">
                              {categories.map((cat) => (
                                <SelectItem key={cat} value={cat}>
                                  {cat}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Title *</Label>
                          <Input
                            value={service.title}
                            onChange={(e) => updateService(index, "title", e.target.value)}
                            placeholder="Service title"
                            className="bg-zinc-950 border-zinc-800 h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <Gauge className="w-3 h-3" /> Interval (miles)
                          </Label>
                          <Input
                            type="number"
                            value={service.interval_miles}
                            onChange={(e) => updateService(index, "interval_miles", e.target.value)}
                            placeholder="e.g., 5000"
                            className="bg-zinc-950 border-zinc-800 h-9 font-mono"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Interval (months)
                          </Label>
                          <Input
                            type="number"
                            value={service.interval_months}
                            onChange={(e) => updateService(index, "interval_months", e.target.value)}
                            placeholder="e.g., 6"
                            className="bg-zinc-950 border-zinc-800 h-9 font-mono"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeEditor}
              className="border-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-template-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
